/**
 * IncomingCallScreen — WhatsApp/Messenger-style full-screen incoming-call UI.
 *
 * Shows:
 *   - Caller avatar (large, pulsing ring)
 *   - Caller display name + username
 *   - "Incoming Video Call" / "Incoming Audio Call" label
 *   - Animated "Ringing…" indicator + elapsed seconds
 *   - Green Accept and Red Decline buttons
 *
 * Receives the audio + vibrate behaviour from useIncomingCallSocket — the
 * hook owns the ringtone and missed-call timer so this UI stays pure.
 */
import React, { useEffect, useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Phone, PhoneOff, Video, MessageSquare } from 'lucide-react';
import { getInitials } from '../../lib/utils';

export default function IncomingCallScreen({ call, onAccept, onDecline }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!call) return;
    const start = Date.now();
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [call?.id]);

  if (!call) return null;

  const isVideo = call.call_type === 'video';
  const displayName = call.caller_display_name || call.caller_username;

  return (
    <div
      data-testid="incoming-call-screen"
      className="fixed inset-0 z-[200] flex flex-col items-center justify-between
                 bg-gradient-to-b from-zinc-900 via-zinc-950 to-black text-white
                 animate-in fade-in duration-200"
      style={{
        // Avoid being scrolled away on mobile when content under it scrolls
        touchAction: 'none',
      }}
    >
      {/* Top — type label */}
      <div className="pt-12 sm:pt-16 w-full text-center px-6">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full
                        bg-white/10 backdrop-blur-sm ring-1 ring-white/15">
          {isVideo ? <Video className="w-4 h-4 text-emerald-400" /> : <Phone className="w-4 h-4 text-emerald-400" />}
          <span className="text-sm font-semibold tracking-wide uppercase">
            {isVideo ? 'Incoming Video Call' : 'Incoming Audio Call'}
          </span>
        </div>
      </div>

      {/* Middle — Caller info */}
      <div className="flex flex-col items-center px-6">
        {/* Pulsing ring */}
        <div className="relative">
          <span className="absolute inset-0 rounded-full bg-emerald-500/30 animate-ping" />
          <span className="absolute inset-0 rounded-full bg-emerald-500/20 animate-pulse [animation-duration:2s]" />
          <Avatar className="w-36 h-36 sm:w-44 sm:h-44 ring-4 ring-emerald-500 ring-offset-4 ring-offset-zinc-950 relative">
            <AvatarImage src={call.caller_avatar} alt={displayName} />
            <AvatarFallback className="text-4xl bg-emerald-500/30 text-white">
              {getInitials(displayName)}
            </AvatarFallback>
          </Avatar>
        </div>

        <h2 className="text-3xl sm:text-4xl font-bold mt-6 text-center" data-testid="caller-name">
          {displayName}
        </h2>
        <p className="text-white/60 mt-1">@{call.caller_username}</p>
        <p className="mt-3 text-emerald-400 font-medium flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
          Ringing… {String(Math.floor(elapsed / 60)).padStart(2, '0')}:{String(elapsed % 60).padStart(2, '0')}
        </p>
      </div>

      {/* Bottom — action buttons */}
      <div className="w-full pb-10 sm:pb-14 px-6">
        <div className="flex items-end justify-between max-w-md mx-auto">
          {/* Decline */}
          <ActionButton
            color="red"
            label="Decline"
            onClick={onDecline}
            testId="decline-call-btn"
            icon={<PhoneOff className="w-7 h-7" />}
          />

          {/* Quick reply (TODO future) */}
          <button
            type="button"
            className="flex flex-col items-center text-white/60 active:text-white transition"
            data-testid="quick-reply-btn"
            onClick={onDecline}
            aria-label="Reply with message"
          >
            <span className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center
                             ring-1 ring-white/10">
              <MessageSquare className="w-5 h-5" />
            </span>
            <span className="text-[11px] mt-1.5">Message</span>
          </button>

          {/* Accept */}
          <ActionButton
            color="green"
            label="Accept"
            onClick={onAccept}
            testId="answer-call-btn"
            icon={isVideo ? <Video className="w-7 h-7" /> : <Phone className="w-7 h-7" />}
          />
        </div>
        <p className="text-center text-white/40 text-xs mt-6">
          Swipe to answer · This call will time out in 45s
        </p>
      </div>
    </div>
  );
}

function ActionButton({ color, label, onClick, icon, testId }) {
  const bg = color === 'green' ? 'bg-emerald-500' : 'bg-red-500';
  const hover = color === 'green' ? 'hover:bg-emerald-600' : 'hover:bg-red-600';
  const glow = color === 'green' ? 'shadow-emerald-500/40' : 'shadow-red-500/40';
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="flex flex-col items-center group active:scale-95 transition-transform"
      aria-label={label}
    >
      <span
        className={`w-16 h-16 sm:w-20 sm:h-20 rounded-full ${bg} ${hover} ${glow}
                    flex items-center justify-center text-white shadow-2xl
                    ring-4 ring-white/5 transition-colors`}
      >
        {icon}
      </span>
      <span className="text-sm font-semibold mt-2">{label}</span>
    </button>
  );
}
